import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { fmtDate } from "@/lib/format";
import { Star, Send, Check, Link2, Save } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { left: "green", clicked: "amber", sent: "navy" };

export default function Reviews() {
  const [rows, setRows] = useState([]);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/reviews").then((r) => { setRows(r.data.requests || []); setLink(r.data.review_link || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveLink = async () => { try { await api.put("/reviews/link", { url: link }); toast.success("Review link saved"); } catch { toast.error("Failed"); } };
  const mark = async (r, status) => { setBusy(r.id); try { await api.put(`/reviews/${r.id}`, { status }); await load(); } catch { toast.error("Failed"); } finally { setBusy(""); } };
  const resend = async (r) => { setBusy(r.id); try { await api.post(`/reviews/${r.id}/resend`); toast.success("Review request re-sent (simulated)"); await load(); } catch { toast.error("Failed"); } finally { setBusy(""); } };

  const left = rows.filter((r) => r.status === "left").length;
  const rate = rows.length ? Math.round((left / rows.length) * 100) : 0;

  return (
    <div>
      <PageHeader title="Review Multiplier" subtitle="Auto-request a Google review the moment a job is paid. Watch your rating climb." />
      <div className="mb-card p-5 mb-6">
        <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Your Google review link</label>
        <div className="flex gap-2 mt-2">
          <input data-testid="review-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://g.page/r/…/review" className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
          <Btn onClick={saveLink} testid="save-review-link"><Save className="w-4 h-4" /> Save</Btn>
        </div>
        <p className="text-xs text-slate-400 mt-2">Requests are texted automatically after a final/full invoice is paid. <span className="font-semibold text-amberw">SMS sending is MOCKED</span> until Twilio is connected.</p>
      </div>

      <div className="flex gap-3 mb-4 text-sm">
        <div className="mb-card px-4 py-2"><span className="text-slate-400 text-xs uppercase font-semibold">Requests</span><div className="font-num font-bold text-navy text-xl" data-testid="review-count">{rows.length}</div></div>
        <div className="mb-card px-4 py-2"><span className="text-slate-400 text-xs uppercase font-semibold">Reviews left</span><div className="font-num font-bold text-successg text-xl">{left} · {rate}%</div></div>
      </div>

      {!rows.length && <div className="mb-card p-10 text-center text-slate-400" data-testid="reviews-empty"><Star className="w-8 h-8 mx-auto mb-2 opacity-50" /> No review requests yet — they appear automatically after a job is paid.</div>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="mb-card p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-2 min-w-0"><Star className={`w-4 h-4 shrink-0 ${r.status === "left" ? "text-amberw fill-amberw" : "text-slate-300"}`} /><span className="font-semibold text-navy truncate">{r.customer_name || "Customer"}</span><span className="text-xs text-slate-400">· {fmtDate(r.created_at)}</span></div>
            <div className="flex items-center gap-2">
              <Pill tone={STATUS_TONE[r.status] || "slate"}>{r.status}</Pill>
              {r.status !== "left" && <button data-testid={`review-resend-${r.id}`} onClick={() => resend(r)} disabled={busy === r.id} className="text-navy hover:text-brandred flex items-center gap-1 text-xs font-semibold disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Resend</button>}
              {r.status !== "left" && <button data-testid={`review-left-${r.id}`} onClick={() => mark(r, "left")} disabled={busy === r.id} className="text-successg hover:scale-110 disabled:opacity-50" title="Mark review left"><Check className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
